import NapsterSpinner from '@components/NapsterSpinner';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
//import { useAuth } from 'react-oidc-context';
import { generatePath, useLocation/*, useNavigate*/ } from 'react-router-dom';
import { placeHolderData } from '../../config';
import {
  deletePlaylist,
  getPlaylists,
  getPlaylistTracks,
} from '../../services/api';
import {
  PlaylistItem,
  removePlaylistFromCategory,
} from '../../store/reducers/commonStore';
import { useAppDispatch } from '../../store/store';
import { analytics } from '../../utils/analytics';
import { showToast } from '../../utils/toast';
import { ConfirmDeletePlaylistModal } from './components/ConfirmDeletePlaylistModal';
import { EditPlaylistModal } from './components/EditPlaylistModal';
import { PlaylistCard } from './components/PlaylistCard';

type FilterTab = 'all' | 'music' | 'serenity' | 'book';

interface CategoryState {
  items: PlaylistItem[];
  loading: boolean;
  hasMore: boolean;
  offset: number;
  total: number;
}

const ITEMS_PER_PAGE = 12;

export const PlaylistsPage = memo(() => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setActivePopup, playlistsByCategory } = useCommonState();
  const { setActivePlaylist, addRecentTrack } = useMusicState();
  /*const { user } = useAuth();
  const navigate = useNavigate();*/
  const { search } = useLocation();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [playingPlaylistId, setPlayingPlaylistId] = useState<string | null>(
    null,
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Delete confirmation modal state
  const [deletingPlaylistId, setDeletingPlaylistId] = useState<string | null>(
    null,
  );
  const [deletingPlaylistName, setDeletingPlaylistName] = useState('');
  const [deletingPlaylistCategory, setDeletingPlaylistCategory] = useState<
    'music' | 'serenity' | 'book' | 'creation' | null
  >(null);
  const [deletingPlaylistTrackCount, setDeletingPlaylistTrackCount] =
    useState<number>(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit modal state
  const [editingPlaylistId, setEditingPlaylistId] = useState<string | null>(
    null,
  );
  const [editingPlaylistName, setEditingPlaylistName] = useState('');
  const [editingPlaylistDescription, setEditingPlaylistDescription] =
    useState('');

  // Local state for pagination and loading
  const [musicPlaylists, setMusicPlaylists] = useState<CategoryState>({
    items: [],
    loading: true,
    hasMore: false,
    offset: 0,
    total: 0,
  });

  const [serenityPlaylists, setSerenityPlaylists] = useState<CategoryState>({
    items: [],
    loading: true,
    hasMore: false,
    offset: 0,
    total: 0,
  });

  const [bookPlaylists, setBookPlaylists] = useState<CategoryState>({
    items: [],
    loading: true,
    hasMore: false,
    offset: 0,
    total: 0,
  });

  // Fetch playlists for a specific category
  const fetchPlaylistsForCategory = useCallback(
    async (
      category: 'music' | 'serenity' | 'book',
      offset: number = 0,
      isLoadMore: boolean = false,
    ) => {
      const setState =
        category === 'music'
          ? setMusicPlaylists
          : category === 'serenity'
            ? setSerenityPlaylists
            : setBookPlaylists;

      if (!isLoadMore) {
        setState((prev) => ({ ...prev, loading: true }));
      }

      try {
        console.log(
          `📡 Fetching ${category} playlists from API (offset: ${offset})...`,
        );
        const response = await getPlaylists(category, ITEMS_PER_PAGE, offset);

        if (response) {
          const newItems = response.items || [];
          const total = response.pagination?.total || 0;
          const hasMore = offset + ITEMS_PER_PAGE < total;

          console.log(`✅ Fetched ${category} playlists:`, newItems.length);

          setState((prev) => ({
            items: isLoadMore ? [...prev.items, ...newItems] : newItems,
            loading: false,
            hasMore,
            offset: offset + ITEMS_PER_PAGE,
            total,
          }));
        }
      } catch (error) {
        console.error(`Error fetching ${category} playlists:`, error);
        showToast.error(t('playlists.failedToLoad', { category }));
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [],
  );

  // Initial load for all categories - only run once on mount
  useEffect(() => {
    console.log('🚀 Initial load: Fetching all category playlists');
    fetchPlaylistsForCategory('music', 0, false);
    fetchPlaylistsForCategory('serenity', 0, false);
    fetchPlaylistsForCategory('book', 0, false);
  }, []);

  // Sync Redux store data to local state when Redux data changes (for add/delete/reorder operations)
  useEffect(() => {
    console.log('🔄 Redux playlists changed, syncing to local state');

    // Update music playlists from Redux if it has changed
    if (playlistsByCategory.music?.items?.length > 0) {
      console.log(
        '🎵 Syncing music playlists from Redux:',
        playlistsByCategory.music.items.length,
      );
      setMusicPlaylists((prev) => ({
        ...prev,
        items: playlistsByCategory.music.items,
      }));
    }

    // Update serenity playlists from Redux if it has changed
    if (playlistsByCategory.serenity?.items?.length > 0) {
      console.log(
        '🧘 Syncing serenity playlists from Redux:',
        playlistsByCategory.serenity.items.length,
      );
      setSerenityPlaylists((prev) => ({
        ...prev,
        items: playlistsByCategory.serenity.items,
      }));
    }

    // Update book playlists from Redux if it has changed
    if (playlistsByCategory.book?.items?.length > 0) {
      console.log(
        '📚 Syncing book playlists from Redux:',
        playlistsByCategory.book.items.length,
      );
      setBookPlaylists((prev) => ({
        ...prev,
        items: playlistsByCategory.book.items,
      }));
    }
  }, [
    playlistsByCategory.music?.items,
    playlistsByCategory.serenity?.items,
    playlistsByCategory.book?.items,
  ]);

  // Listen for playlist updates from other components (e.g., when track is added from minimized player)
  useEffect(() => {
    const handlePlaylistUpdated = (event: any) => {
      console.log('📢 Playlist updated event received', event.detail);

      // Get the playlist ID from the event detail if available
      const playlistId = event.detail?.playlistId;

      if (playlistId) {
        // Refetch the specific playlist to update thumbnail and track count
        console.log('🎯 Refetching specific playlist:', playlistId);

        // Determine which category the playlist belongs to and refetch
        const refetchPlaylist = async () => {
          try {
            // Try to find and refetch the playlist from each category
            const categories: Array<'music' | 'serenity' | 'book'> = [
              'music',
              'serenity',
              'book',
            ];

            for (const category of categories) {
              const response = await getPlaylists(category, 50, 0);
              const updatedPlaylist = response?.items?.find(
                (p) => p.id === playlistId,
              );

              if (updatedPlaylist) {
                // Update the playlist in the appropriate category
                if (category === 'music') {
                  setMusicPlaylists((prev) => ({
                    ...prev,
                    items: prev.items.map((p) =>
                      p.id === playlistId ? updatedPlaylist : p,
                    ),
                  }));
                } else if (category === 'serenity') {
                  setSerenityPlaylists((prev) => ({
                    ...prev,
                    items: prev.items.map((p) =>
                      p.id === playlistId ? updatedPlaylist : p,
                    ),
                  }));
                } else if (category === 'book') {
                  setBookPlaylists((prev) => ({
                    ...prev,
                    items: prev.items.map((p) =>
                      p.id === playlistId ? updatedPlaylist : p,
                    ),
                  }));
                }
                break; // Found the playlist, no need to check other categories
              }
            }
          } catch (error) {
            console.error('Error refetching playlist:', error);
          }
        };

        refetchPlaylist();
      } else {
        // Fallback: refresh all playlists if no specific playlist ID provided (e.g., when track is deleted)
        console.log('🔄 Refreshing all playlists');
        fetchPlaylistsForCategory('music', 0, false);
        fetchPlaylistsForCategory('serenity', 0, false);
        fetchPlaylistsForCategory('book', 0, false);
      }
    };

    window.addEventListener('playlistUpdated', handlePlaylistUpdated);
    return () => {
      window.removeEventListener('playlistUpdated', handlePlaylistUpdated);
    };
  }, [
    fetchPlaylistsForCategory,
    musicPlaylists.items,
    serenityPlaylists.items,
    bookPlaylists.items,
  ]);

  // Play playlist
  const playPlaylist = useCallback(
    async (playlist: PlaylistItem) => {
      try {
        setPlayingPlaylistId(playlist.id);

        // Fetch full playlist data with tracks
        const playlistData = await getPlaylistTracks(playlist.id);

        if (
          !playlistData ||
          !playlistData.tracks ||
          playlistData.tracks.length === 0
        ) {
          showToast.error(t('playlists.noTracksInPlaylist'));
          setPlayingPlaylistId(null);
          return;
        }

        const firstTrack = playlistData.tracks[0];
        const contributor =
          firstTrack.contributors && firstTrack.contributors.length > 0
            ? firstTrack.contributors[0]
            : null;

        // Create playlist object for music player
        const playlistObj = {
          currentIndex: 0,
          album: {
            id: playlistData.id,
            albumName: playlistData.name,
            albumArtUrl:
              firstTrack.album?.thumbnailUrl || placeHolderData.albumCover,
            contributors: firstTrack.contributors || [],
            tracks: playlistData.tracks,
            category: playlistData.category,
            likes: 0,
            isLiked: false,
            upc: '',
            pk: '',
            description: playlistData.description || '',
            genre: [],
            type: 'playlist',
            privacy: playlistData.privacy,
          },
        };

        // Add first track to recent
        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: firstTrack.url,
            trackIndex: 1,
          },
          contributor: {
            id: contributor?.id || '',
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            firstTrack.album?.thumbnailUrl || placeHolderData.albumCover,
          timestamp: Date.now(),
        };

        addRecentTrack(recentTrack);
        setActivePlaylist(playlistObj);
        setActivePopup('musicplayer');

        // Track playlist playback started (use canonical playlist URL as source)
        analytics.logEvent('playlist_playback_started', {
          playlist_id: playlist.id,
          first_track_id: firstTrack.id,
          source:
            generatePath('/playlist/:id/:trackId', {
              id: playlist.id,
              trackId: firstTrack.id,
            }) + (search || ''),
        });
      } catch (error) {
        console.error('Error playing playlist:', error);
        showToast.error(t('playlists.failedToPlay'));
      } finally {
        setPlayingPlaylistId(null);
      }
    },
    [setActivePlaylist, addRecentTrack, setActivePopup, search],
  );

  // Handle edit playlist - show modal
  const handleEditPlaylist = useCallback(
    (playlistId: string, playlistName: string, playlistDescription: string) => {
      setEditingPlaylistId(playlistId);
      setEditingPlaylistName(playlistName);
      setEditingPlaylistDescription(playlistDescription);
    },
    [],
  );

  // Handle delete playlist - show confirmation
  const handleDeletePlaylist = useCallback(
    (
      playlistId: string,
      playlistName: string,
      category: 'music' | 'serenity' | 'book' | 'creation',
    ) => {
      let trackCount = 0;
      if (category === 'creation') {
        const playlist = playlistsByCategory.creation?.items.find(
          (p) => p.id === playlistId,
        );
        if (playlist) {
          trackCount = playlist.trackCount;
        }
      } else {
        const categoryState = getCategoryState(category);
        const playlist = categoryState.items.find((p) => p.id === playlistId);
        if (playlist) {
          trackCount = playlist.trackCount;
        }
      }

      setDeletingPlaylistId(playlistId);
      setDeletingPlaylistName(playlistName);
      setDeletingPlaylistCategory(category);
      setDeletingPlaylistTrackCount(trackCount);
    },
    [
      musicPlaylists,
      serenityPlaylists,
      bookPlaylists,
      playlistsByCategory.creation?.items,
    ],
  );

  // Handle confirmed delete
  const handleConfirmDeletePlaylist = useCallback(
    async (playlistId: string) => {
      setIsDeleting(true);
      try {
        await deletePlaylist(playlistId);
        showToast.success(t('playlists.deletedSuccessfully'));

        // Track playlist deletion event
        analytics.logEvent('playlist_deleted', {
          playlist_id: playlistId,
          track_count: deletingPlaylistTrackCount,
        });

        // Update Redux store first to keep AddToPlaylistModal in sync
        if (deletingPlaylistCategory) {
          dispatch(
            removePlaylistFromCategory({
              category: deletingPlaylistCategory,
              playlistId,
            }),
          );
        }

        // Then update the local state for this page
        if (deletingPlaylistCategory === 'music') {
          setMusicPlaylists((prev) => ({
            ...prev,
            items: prev.items.filter((p) => p.id !== playlistId),
            total: Math.max(0, prev.total - 1),
          }));
        } else if (deletingPlaylistCategory === 'serenity') {
          setSerenityPlaylists((prev) => ({
            ...prev,
            items: prev.items.filter((p) => p.id !== playlistId),
            total: Math.max(0, prev.total - 1),
          }));
        } else if (deletingPlaylistCategory === 'book') {
          setBookPlaylists((prev) => ({
            ...prev,
            items: prev.items.filter((p) => p.id !== playlistId),
            total: Math.max(0, prev.total - 1),
          }));
        }
        // creation (and other store-only categories) are updated via Redux above

        setDeletingPlaylistId(null);
        setDeletingPlaylistCategory(null);
        setDeletingPlaylistTrackCount(0);
      } catch (error) {
        console.error('Error deleting playlist:', error);
        showToast.error(t('playlists.failedToDelete'));
      } finally {
        setIsDeleting(false);
      }
    },
    [deletingPlaylistCategory, deletingPlaylistTrackCount, dispatch],
  );

  // Get current category state
  const getCategoryState = (category: 'music' | 'serenity' | 'book') => {
    switch (category) {
      case 'music':
        return musicPlaylists;
      case 'serenity':
        return serenityPlaylists;
      case 'book':
        return bookPlaylists;
    }
  };

  // Get filtered playlists based on active tab
  const getFilteredPlaylists = () => {
    if (activeTab === 'all') {
      return null; // Show all categories
    }
    return getCategoryState(activeTab as 'music' | 'serenity' | 'book');
  };

  const isInitialLoading =
    musicPlaylists.loading &&
    musicPlaylists.items.length === 0 &&
    serenityPlaylists.loading &&
    serenityPlaylists.items.length === 0 &&
    bookPlaylists.loading &&
    bookPlaylists.items.length === 0;

  const filterTabs = [
    { id: 'all' as FilterTab, label: t('playlists.tabAll') },
    {
      id: 'music' as FilterTab,
      label: t('playlists.tabMusic'),
      count: musicPlaylists.total,
    },
    {
      id: 'serenity' as FilterTab,
      label: t('playlists.tabSerenity'),
      count: serenityPlaylists.total,
    },
    {
      id: 'book' as FilterTab,
      label: t('playlists.tabAudiobooks'),
      count: bookPlaylists.total,
    },
  ];

  return (
    <div className="flex flex-col h-full w-full pt-0 sm:pt-[10px]">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6">
        <div className="flex flex-col">
          <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
            {t('playlists.title')}
          </h1>
          <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
            {t('playlists.subtitle')}
          </div>
        </div>

        {/* Banner for old music users */}
        {/*{user?.profile?.isOldMusicUser === true && (
          <div className="mb-4 rounded-2xl bg-white/10 text-white hover:bg-white/20 p-4 flex items-center justify-between">
            <div className="text-[#fcfcfc] font-[NapsterSans] text-sm sm:text-base">
              Missing your old playlists?
            </div>
            <button
              onClick={() => navigate('/oldplaylists')}
              className="px-4 py-2 bg-white text-black rounded-full text-sm font-medium hover:bg-white/90 transition-colors font-[NapsterSans]">
              Learn more
            </button>
          </div>
        )}*/}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-black'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}>
              {tab.label}
              {tab.id !== 'all' && (
                <span className="ml-2 text-xs opacity-70">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto na-scroll-container">
        <div className="px-4 sm:px-6 pb-[125px]">
          {/* Loading State */}
          {isInitialLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
              {[...Array(8)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="flex flex-col animate-pulse">
                  <div className="w-full h-[520px] rounded-[16px] bg-gray-700"></div>
                </div>
              ))}
            </div>
          )}

          {/* Content when not loading */}
          {!isInitialLoading && (
            <>
              {/* Show all categories when "All" tab is active */}
              {activeTab === 'all' ? (
                <div className="space-y-8">
                  {/* Music Playlists */}
                  {musicPlaylists.items.length > 0 && (
                    <div>
                      <h3 className="text-white text-xl text-left font-bold mb-4">
                        {t('playlists.musicCount', { count: musicPlaylists.total })}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {musicPlaylists.items.map((playlist) => {
                          const thumbnailUrl =
                            playlist.thumbnailUrl || placeHolderData.albumCover;

                          return (
                            <PlaylistCard
                              key={`music-${playlist.id}`}
                              playlist={playlist}
                              playlistType="music"
                              thumbnailUrl={thumbnailUrl}
                              playingPlaylistId={playingPlaylistId}
                              isDeleting={isDeleting}
                              onPlay={playPlaylist}
                              onEdit={handleEditPlaylist}
                              onDelete={handleDeletePlaylist}
                            />
                          );
                        })}
                      </div>

                      {/* Load More Button for Music */}
                      {musicPlaylists.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() =>
                              fetchPlaylistsForCategory(
                                'music',
                                musicPlaylists.offset,
                                true,
                              )
                            }
                            disabled={musicPlaylists.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {musicPlaylists.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              t('playlists.loadMoreMusic')
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Serenity Playlists */}
                  {serenityPlaylists.items.length > 0 && (
                    <div>
                      <h3 className="text-white text-xl text-left font-bold mb-4">
                        {t('playlists.serenityCount', { count: serenityPlaylists.total })}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {serenityPlaylists.items.map((playlist) => {
                          const thumbnailUrl =
                            playlist.thumbnailUrl || placeHolderData.albumCover;

                          return (
                            <PlaylistCard
                              key={`serenity-${playlist.id}`}
                              playlist={playlist}
                              playlistType="serenity"
                              thumbnailUrl={thumbnailUrl}
                              playingPlaylistId={playingPlaylistId}
                              isDeleting={isDeleting}
                              onPlay={playPlaylist}
                              onEdit={handleEditPlaylist}
                              onDelete={handleDeletePlaylist}
                            />
                          );
                        })}
                      </div>

                      {/* Load More Button for Serenity */}
                      {serenityPlaylists.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() =>
                              fetchPlaylistsForCategory(
                                'serenity',
                                serenityPlaylists.offset,
                                true,
                              )
                            }
                            disabled={serenityPlaylists.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {serenityPlaylists.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              t('playlists.loadMoreSerenity')
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Audiobook Playlists */}
                  {bookPlaylists.items.length > 0 && (
                    <div>
                      <h3 className="text-white text-xl text-left font-bold mb-4">
                        {t('playlists.audiobooksCount', { count: bookPlaylists.total })}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {bookPlaylists.items.map((playlist) => {
                          const thumbnailUrl =
                            playlist.thumbnailUrl || placeHolderData.albumCover;

                          return (
                            <PlaylistCard
                              key={`book-${playlist.id}`}
                              playlist={playlist}
                              playlistType="book"
                              thumbnailUrl={thumbnailUrl}
                              playingPlaylistId={playingPlaylistId}
                              isDeleting={isDeleting}
                              onPlay={playPlaylist}
                              onEdit={handleEditPlaylist}
                              onDelete={handleDeletePlaylist}
                            />
                          );
                        })}
                      </div>

                      {/* Load More Button for Audiobook */}
                      {bookPlaylists.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() =>
                              fetchPlaylistsForCategory(
                                'book',
                                bookPlaylists.offset,
                                true,
                              )
                            }
                            disabled={bookPlaylists.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {bookPlaylists.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              t('playlists.loadMoreAudiobooks')
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Empty state for All tab */}
                  {musicPlaylists.items.length === 0 &&
                    serenityPlaylists.items.length === 0 &&
                    bookPlaylists.items.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-16">
                        <h3 className="text-white text-lg font-medium mb-2">
                          {t('playlists.noPlaylistsYet')}
                        </h3>
                        <p className="text-white/60 text-sm text-center max-w-sm">
                          {t('playlists.createFirstPlaylist')}
                        </p>
                      </div>
                    )}
                </div>
              ) : (
                // Show single category
                <div>
                  {getFilteredPlaylists()?.items && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                        {getFilteredPlaylists()!.items.map((playlist) => {
                          const thumbnailUrl =
                            playlist.thumbnailUrl || placeHolderData.albumCover;

                          return (
                            <PlaylistCard
                              key={playlist.id}
                              playlist={playlist}
                              playlistType={
                                activeTab as 'music' | 'serenity' | 'book'
                              }
                              thumbnailUrl={thumbnailUrl}
                              playingPlaylistId={playingPlaylistId}
                              isDeleting={isDeleting}
                              onPlay={playPlaylist}
                              onEdit={handleEditPlaylist}
                              onDelete={handleDeletePlaylist}
                            />
                          );
                        })}
                      </div>

                      {/* Load More Button for single category */}
                      {getFilteredPlaylists()?.hasMore && (
                        <div className="flex justify-center mt-6">
                          <button
                            onClick={() => {
                              const category = activeTab as
                                | 'music'
                                | 'serenity'
                                | 'book';
                              const state = getCategoryState(category);
                              fetchPlaylistsForCategory(
                                category,
                                state.offset,
                                true,
                              );
                            }}
                            disabled={getFilteredPlaylists()?.loading}
                            className="px-8 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 text-white rounded-lg transition-colors duration-200 disabled:cursor-not-allowed">
                            {getFilteredPlaylists()?.loading ? (
                              <div className="flex items-center gap-2">
                                <NapsterSpinner size="small" />
                                {t('common.loading')}
                              </div>
                            ) : (
                              activeTab === 'music'
                                ? t('playlists.loadMoreMusic')
                                : activeTab === 'serenity'
                                  ? t('playlists.loadMoreSerenity')
                                  : t('playlists.loadMoreAudiobooks')
                            )}
                          </button>
                        </div>
                      )}

                      {/* Empty state for single category */}
                      {getFilteredPlaylists()?.items.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16">
                          <h3 className="text-white text-lg font-medium mb-2">
                            {t('playlists.noPlaylistsInCategory')}
                          </h3>
                          <p className="text-white/60 text-sm text-center max-w-sm">
                            {t('playlists.createPlaylistToStart')}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm Delete Playlist Modal */}
      <ConfirmDeletePlaylistModal
        isOpen={deletingPlaylistId !== null}
        playlistName={deletingPlaylistName}
        onConfirm={() => {
          if (deletingPlaylistId) {
            handleConfirmDeletePlaylist(deletingPlaylistId);
          }
        }}
        onCancel={() => {
          setDeletingPlaylistId(null);
          setDeletingPlaylistName('');
          setDeletingPlaylistCategory(null);
          setDeletingPlaylistTrackCount(0);
        }}
        isLoading={isDeleting}
      />

      {/* Edit Playlist Modal */}
      <EditPlaylistModal
        isOpen={editingPlaylistId !== null}
        onClose={() => {
          setEditingPlaylistId(null);
          setEditingPlaylistName('');
          setEditingPlaylistDescription('');
        }}
        playlistId={editingPlaylistId || ''}
        initialName={editingPlaylistName}
        initialDescription={editingPlaylistDescription}
        onSuccess={() => {
          // Refresh playlists after successful edit
          fetchPlaylistsForCategory('music', 0, false);
          fetchPlaylistsForCategory('serenity', 0, false);
          fetchPlaylistsForCategory('book', 0, false);
        }}
      />
    </div>
  );
});

PlaylistsPage.displayName = 'PlaylistsPage';

export default PlaylistsPage;
